import React, { useState, useEffect } from 'react'
import {
  Container,
  Header,
  SpaceBetween,
  Tabs,
  Table,
  Badge,
  StatusIndicator,
  Box,
  Button,
  ColumnLayout,
  ExpandableSection,
  CodeEditor,
  Alert
} from '@cloudscape-design/components'
import { apiService } from '../services/api'

interface DiagnosticData {
  dataProduct: string
  locationId: string
  locationName: string
  location_type?: string  // Station type from location metadata (e.g., "fixed", "buoy")
  status: 'current' | 'out_of_date' | 'error'
  lastUpdate: string
  payload: any
}

const Diagnostics: React.FC = () => {
  const [selectedDataProduct, setSelectedDataProduct] = useState('metar')
  const [diagnosticData, setDiagnosticData] = useState<DiagnosticData[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [alert, setAlert] = useState<{ type: 'success' | 'error', message: string } | null>(null)
  const [systemHealth, setSystemHealth] = useState({
    dataCollection: 'Running',
    lastCollectionCycle: 'Loading...'
  })
  
  // HF Propagation summary state
  const [hfPropagationSummary, setHfPropagationSummary] = useState<any>(null)
  const [hfPropagationLoading, setHfPropagationLoading] = useState(false)
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('')
  
  // Pagination state for each data product - using tokens instead of page numbers
  const [currentPageMetar, setCurrentPageMetar] = useState(1)
  const [currentPageMarine, setCurrentPageMarine] = useState(1)
  const [currentPageTrails, setCurrentPageTrails] = useState(1)
  const [currentPageNws, setCurrentPageNws] = useState(1)
  const [nextTokenMetar, setNextTokenMetar] = useState<string | undefined>(undefined)
  const [nextTokenMarine, setNextTokenMarine] = useState<string | undefined>(undefined)
  const [nextTokenTrails, setNextTokenTrails] = useState<string | undefined>(undefined)
  const [nextTokenNws, setNextTokenNws] = useState<string | undefined>(undefined)
  const [tokenHistoryMetar, setTokenHistoryMetar] = useState<(string | undefined)[]>([undefined])
  const [tokenHistoryMarine, setTokenHistoryMarine] = useState<(string | undefined)[]>([undefined])
  const [tokenHistoryTrails, setTokenHistoryTrails] = useState<(string | undefined)[]>([undefined])
  const [tokenHistoryNws, setTokenHistoryNws] = useState<(string | undefined)[]>([undefined])
  const [currentPageBeachConditions, setCurrentPageBeachConditions] = useState(1)
  const [nextTokenBeachConditions, setNextTokenBeachConditions] = useState<string | undefined>(undefined)
  const [tokenHistoryBeachConditions, setTokenHistoryBeachConditions] = useState<(string | undefined)[]>([undefined])
  const itemsPerPage = 25

  useEffect(() => {
    loadDiagnosticData()
    // Load first page of METAR data by default
    loadPageData('metar', undefined)
  }, [])

  const handlePageChange = (product: string, direction: 'next' | 'prev') => {
    let currentPage = 1
    let tokenHistory: (string | undefined)[] = []
    let nextToken: string | undefined
    
    if (product === 'metar') {
      currentPage = currentPageMetar
      tokenHistory = tokenHistoryMetar
      nextToken = nextTokenMetar
    } else if (product === 'marine') {
      currentPage = currentPageMarine
      tokenHistory = tokenHistoryMarine
      nextToken = nextTokenMarine
    } else if (product === 'trail') {
      currentPage = currentPageTrails
      tokenHistory = tokenHistoryTrails
      nextToken = nextTokenTrails
    } else if (product === 'nws') {
      currentPage = currentPageNws
      tokenHistory = tokenHistoryNws
      nextToken = nextTokenNws
    } else if (product === 'beach_conditions') {
      currentPage = currentPageBeachConditions
      tokenHistory = tokenHistoryBeachConditions
      nextToken = nextTokenBeachConditions
    }
    
    if (direction === 'next' && nextToken) {
      // Going forward - use the next token
      const newPage = currentPage + 1
      const newTokenHistory = [...tokenHistory, nextToken]
      
      if (product === 'metar') {
        setCurrentPageMetar(newPage)
        setTokenHistoryMetar(newTokenHistory)
      } else if (product === 'marine') {
        setCurrentPageMarine(newPage)
        setTokenHistoryMarine(newTokenHistory)
      } else if (product === 'trail') {
        setCurrentPageTrails(newPage)
        setTokenHistoryTrails(newTokenHistory)
      } else if (product === 'nws') {
        setCurrentPageNws(newPage)
        setTokenHistoryNws(newTokenHistory)
      } else if (product === 'beach_conditions') {
        setCurrentPageBeachConditions(newPage)
        setTokenHistoryBeachConditions(newTokenHistory)
      }
      
      loadPageData(product, nextToken)
    } else if (direction === 'prev' && currentPage > 1) {
      // Going backward - use token from history
      const newPage = currentPage - 1
      const newTokenHistory = tokenHistory.slice(0, -1)
      const prevToken = newTokenHistory[newTokenHistory.length - 1]
      
      if (product === 'metar') {
        setCurrentPageMetar(newPage)
        setTokenHistoryMetar(newTokenHistory)
      } else if (product === 'marine') {
        setCurrentPageMarine(newPage)
        setTokenHistoryMarine(newTokenHistory)
      } else if (product === 'trail') {
        setCurrentPageTrails(newPage)
        setTokenHistoryTrails(newTokenHistory)
      } else if (product === 'nws') {
        setCurrentPageNws(newPage)
        setTokenHistoryNws(newTokenHistory)
      } else if (product === 'beach_conditions') {
        setCurrentPageBeachConditions(newPage)
        setTokenHistoryBeachConditions(newTokenHistory)
      }
      
      loadPageData(product, prevToken)
    }
  }

  const loadDiagnosticData = async () => {
    setLoading(true)
    setAlert(null)

    try {
      const dataProducts = ['metar', 'marine', 'trail', 'nws', 'beach_conditions']

      // Load all diagnostics in parallel instead of sequentially
      await Promise.all(dataProducts.map(async (product) => {
        try {
          // Get diagnostics for this data product
          const diagnosticsResponse = await apiService.getDiagnostics(product)
          
          if (diagnosticsResponse.statusCode === 200 && diagnosticsResponse.data) {
            const diagnostics = diagnosticsResponse.data
            
            // Get the most recent data timestamp from latest_api_responses
            if (diagnostics.latest_api_responses && diagnostics.latest_api_responses.length > 0) {
              const mostRecentTimestamp = diagnostics.latest_api_responses[0].timestamp
              const lastCollectionTime = new Date(mostRecentTimestamp)
              const now = new Date()
              const diffMinutes = Math.floor((now.getTime() - lastCollectionTime.getTime()) / (1000 * 60))
              
              let timeAgo = ''
              if (diffMinutes < 1) {
                timeAgo = 'Just now'
              } else if (diffMinutes < 60) {
                timeAgo = `${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''} ago`
              } else if (diffMinutes < 1440) {
                const hours = Math.floor(diffMinutes / 60)
                timeAgo = `${hours} hour${hours !== 1 ? 's' : ''} ago`
              } else {
                const days = Math.floor(diffMinutes / 1440)
                timeAgo = `${days} day${days !== 1 ? 's' : ''} ago`
              }
              
              setSystemHealth(prev => ({
                ...prev,
                dataCollection: 'Running',
                lastCollectionCycle: timeAgo
              }))
            }
          }
        } catch (error) {
          console.error(`Error loading diagnostics for ${product}:`, error)
        }
      }))
    } catch (error) {
      console.error('Error loading diagnostic data:', error)
      setAlert({
        type: 'error',
        message: 'Failed to load diagnostic data. Please try again.'
      })
    } finally {
      setLoading(false)
    }
  }

  const loadPageData = async (product: string, nextToken?: string, searchQuery?: string) => {
    try {
      setLoading(true)
      console.log(`Loading page data for ${product}, nextToken: ${nextToken}, searchQuery: ${searchQuery}`)
      
      // Get historical data for this page
      const historyResponse = await apiService.getDiagnosticsHistory(product, itemsPerPage, nextToken, searchQuery)
      
      console.log(`History response for ${product}:`, historyResponse)
      
      if (historyResponse.statusCode === 200 && historyResponse.data) {
        const responseData = historyResponse.data as any
        const pageData: DiagnosticData[] = []
        
        console.log(`Response data:`, responseData)
        
        // Extract records from historical_summary structure
        if (responseData.historical_summary && Array.isArray(responseData.historical_summary)) {
          console.log(`Found ${responseData.historical_summary.length} historical summaries`)
          
          responseData.historical_summary.forEach((summary: any) => {
            const locationId = summary.location_id
            const locationName = summary.location_name || locationId
            
            // Get the most recent record from sample_records
            let recentData = null
            if (summary.sample_records && summary.sample_records.length > 0) {
              recentData = summary.sample_records[0]
            }

            // Determine status based on last update
            let status: 'current' | 'out_of_date' | 'error' = 'error'
            let lastUpdate = 'Never'

            if (recentData) {
              const lastUpdateTime = new Date(recentData.timestamp || recentData.updated_at)
              const now = new Date()
              const diffMinutes = (now.getTime() - lastUpdateTime.getTime()) / (1000 * 60)
              
              if (diffMinutes < 60) {
                status = 'current'
                lastUpdate = `${Math.floor(diffMinutes)} minutes ago`
              } else if (diffMinutes < 1440) {
                status = 'out_of_date'
                lastUpdate = `${Math.floor(diffMinutes / 60)} hours ago`
              } else {
                status = 'error'
                lastUpdate = `${Math.floor(diffMinutes / 1440)} days ago`
              }
            }

            pageData.push({
              dataProduct: product,
              locationId,
              locationName,
              location_type: summary.location_type,  // Include type from location metadata
              status,
              lastUpdate,
              payload: recentData?.payload_summary || recentData?.payload || {}
            })
          })
        }
        
        // Update diagnostic data for this product
        setDiagnosticData(prev => {
          // Remove old data for this product and add new page data
          const filtered = prev.filter(item => item.dataProduct !== product)
          return [...filtered, ...pageData]
        })
        
        // Store pagination info
        if (responseData.pagination) {
          const newNextToken = responseData.pagination.next_token
          if (product === 'metar') {
            setNextTokenMetar(newNextToken)
          } else if (product === 'marine') {
            setNextTokenMarine(newNextToken)
          } else if (product === 'trail') {
            setNextTokenTrails(newNextToken)
          } else if (product === 'nws') {
            setNextTokenNws(newNextToken)
          } else if (product === 'beach_conditions') {
            setNextTokenBeachConditions(newNextToken)
          }
        }
      }
    } catch (error) {
      console.error(`Error loading page data for ${product}:`, error)
      setAlert({
        type: 'error',
        message: `Failed to load data for ${product}. Please try again.`
      })
    } finally {
      // Always turn off loading state
      setLoading(false)
    }
  }

  const loadHfPropagationSummary = async () => {
    setHfPropagationLoading(true)
    try {
      const response = await apiService.getDiagnostics('hf_propagation', true)
      if (response.statusCode === 200 && response.data) {
        setHfPropagationSummary(response.data)
      }
    } catch (error) {
      console.error('Error loading HF Propagation summary:', error)
    } finally {
      setHfPropagationLoading(false)
    }
  }

  const refreshData = async () => {
    setRefreshing(true)
    
    // Load diagnostic data and all page data in parallel
    const metarToken = tokenHistoryMetar[tokenHistoryMetar.length - 1]
    const marineToken = tokenHistoryMarine[tokenHistoryMarine.length - 1]
    const trailToken = tokenHistoryTrails[tokenHistoryTrails.length - 1]
    const nwsToken = tokenHistoryNws[tokenHistoryNws.length - 1]
    const beachConditionsToken = tokenHistoryBeachConditions[tokenHistoryBeachConditions.length - 1]
    
    await Promise.all([
      loadDiagnosticData(),
      loadPageData('metar', metarToken),
      loadPageData('marine', marineToken),
      loadPageData('trail', trailToken),
      loadPageData('nws', nwsToken),
      loadPageData('beach_conditions', beachConditionsToken)
    ])
    
    setRefreshing(false)
    
    setAlert({
      type: 'success',
      message: 'Diagnostic data refreshed successfully'
    })
  }

  const getStatusIndicator = (status: string) => {
    switch (status) {
      case 'current':
        return <StatusIndicator type="success">Current</StatusIndicator>
      case 'out_of_date':
        return <StatusIndicator type="warning">Out of Date</StatusIndicator>
      case 'error':
        return <StatusIndicator type="error">Error</StatusIndicator>
      default:
        return <StatusIndicator type="pending">Unknown</StatusIndicator>
    }
  }

  const handleSearch = () => {
    if (searchQuery.trim()) {
      setLoading(true)
      if (selectedDataProduct === 'metar') {
        loadPageData('metar', undefined, searchQuery.trim())
      } else if (selectedDataProduct === 'marine') {
        loadPageData('marine', undefined, searchQuery.trim())
      } else if (selectedDataProduct === 'nws') {
        loadPageData('nws', undefined, searchQuery.trim())
      } else if (selectedDataProduct === 'beach_conditions') {
        loadPageData('beach_conditions', undefined, searchQuery.trim())
      }
    }
  }

  const handleClearSearch = () => {
    setSearchQuery('')
    setLoading(true)
    if (selectedDataProduct === 'metar') {
      loadPageData('metar', undefined)
    } else if (selectedDataProduct === 'marine') {
      loadPageData('marine', undefined)
    } else if (selectedDataProduct === 'nws') {
      loadPageData('nws', undefined)
    } else if (selectedDataProduct === 'beach_conditions') {
      loadPageData('beach_conditions', undefined)
    }
  }

  const filteredData = diagnosticData.filter(item => item.dataProduct === selectedDataProduct)

  return (
    <SpaceBetween size="l">
      <Header variant="h1">System Diagnostics</Header>

      {alert && (
        <Alert
          type={alert.type}
          dismissible
          onDismiss={() => setAlert(null)}
        >
          {alert.message}
        </Alert>
      )}

      <Container header={<Header variant="h2">System Overview</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Data Collection Status</Box>
            <StatusIndicator type={systemHealth.dataCollection === 'Running' ? 'success' : 'error'}>
              {systemHealth.dataCollection}
            </StatusIndicator>
          </div>
          <div>
            <Box variant="awsui-key-label">Last Collection Cycle</Box>
            <div>{systemHealth.lastCollectionCycle}</div>
          </div>
        </ColumnLayout>
      </Container>

      <Tabs
        tabs={[
          {
            label: 'METAR Weather',
            id: 'metar',
            content: (
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <Button 
                        onClick={refreshData}
                        loading={refreshing}
                      >
                        Refresh Data
                      </Button>
                    }
                  >
                    METAR Weather Diagnostics
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <SpaceBetween direction="horizontal" size="xs">
                    <input
                      type="text"
                      placeholder="Search by location code (e.g., KGRB)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch()
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d5dbdb',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <Button onClick={handleSearch} disabled={!searchQuery.trim()}>
                      Search
                    </Button>
                    {searchQuery && (
                      <Button onClick={handleClearSearch}>
                        Clear
                      </Button>
                    )}
                  </SpaceBetween>
                  <Table
                  columnDefinitions={[
                    {
                      id: 'locationId',
                      header: 'Location ID',
                      cell: item => <code>{item.locationId}</code>
                    },
                    {
                      id: 'locationName',
                      header: 'Location Name',
                      cell: item => item.locationName
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: item => getStatusIndicator(item.status)
                    },
                    {
                      id: 'lastUpdate',
                      header: 'Last Update',
                      cell: item => item.lastUpdate
                    },
                    {
                      id: 'flightCategory',
                      header: 'Flight Category',
                      cell: item => {
                        const category = item.payload?.flightCategory
                        let color: 'green' | 'blue' | 'red' | 'grey' = 'grey'
                        switch (category) {
                          case 'VFR': color = 'green'; break
                          case 'MVFR': color = 'blue'; break
                          case 'IFR': color = 'red'; break
                          case 'LIFR': color = 'red'; break
                        }
                        return <Badge color={color}>{category || 'Unknown'}</Badge>
                      }
                    }
                  ]}
                  items={diagnosticData.filter(item => item.dataProduct === 'metar')}
                  loading={loading}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No METAR data</b>
                      <Box variant="p" color="inherit">
                        No METAR weather data is currently available.
                      </Box>
                    </Box>
                  }
                  footer={
                    <Box textAlign="center" padding="s">
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          disabled={currentPageMetar === 1}
                          onClick={() => handlePageChange('metar', 'prev')}
                        >
                          Previous
                        </Button>
                        <Box variant="span">Page {currentPageMetar}</Box>
                        <Button
                          disabled={!nextTokenMetar}
                          onClick={() => handlePageChange('metar', 'next')}
                        >
                          Next
                        </Button>
                      </SpaceBetween>
                    </Box>
                  }
                />
                </SpaceBetween>
              </Container>
            )
          },
          {
            label: 'Marine Conditions',
            id: 'marine',
            content: (
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <Button 
                        onClick={refreshData}
                        loading={refreshing}
                      >
                        Refresh Data
                      </Button>
                    }
                  >
                    Marine Conditions Diagnostics
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <SpaceBetween direction="horizontal" size="xs">
                    <input
                      type="text"
                      placeholder="Search by station ID (e.g., 46042)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch()
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d5dbdb',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <Button onClick={handleSearch} disabled={!searchQuery.trim()}>
                      Search
                    </Button>
                    {searchQuery && (
                      <Button onClick={handleClearSearch}>
                        Clear
                      </Button>
                    )}
                  </SpaceBetween>
                  <Table
                  columnDefinitions={[
                    {
                      id: 'locationId',
                      header: 'Station ID',
                      cell: item => <code>{item.locationId}</code>
                    },
                    {
                      id: 'locationName',
                      header: 'Station Name',
                      cell: item => item.locationName
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: item => getStatusIndicator(item.status)
                    },
                    {
                      id: 'lastUpdate',
                      header: 'Last Update',
                      cell: item => item.lastUpdate
                    },
                    {
                      id: 'stationType',
                      header: 'Type',
                      cell: item => {
                        // Use location_type from metadata (authoritative source) instead of payload
                        const stationType = item.location_type || item.payload?.stationType || 'Unknown';
                        return (
                          <Badge color={stationType === 'buoy' ? 'blue' : 'green'}>
                            {stationType}
                          </Badge>
                        );
                      }
                    },
                    {
                      id: 'airTemperature',
                      header: 'Air Temp',
                      cell: item => {
                        const temp = item.payload?.airTemperature
                        if (temp === null || temp === undefined || temp === 'N/A') {
                          return 'N/A'
                        }
                        return `${temp}°F`
                      }
                    },
                    {
                      id: 'waterTemperature',
                      header: 'Water Temp',
                      cell: item => {
                        const temp = item.payload?.waterTemperature
                        if (temp === null || temp === undefined || temp === 'N/A') {
                          return 'N/A'
                        }
                        return `${temp}°F`
                      }
                    },
                    {
                      id: 'waveHeight',
                      header: 'Wave Height',
                      cell: item => {
                        const height = item.payload?.waveHeight
                        if (height === null || height === undefined || height === 'N/A') {
                          return 'N/A'
                        }
                        return `${height} m`
                      }
                    }
                  ]}
                  items={diagnosticData.filter(item => item.dataProduct === 'marine')}
                  loading={loading}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No marine data</b>
                      <Box variant="p" color="inherit">
                        No marine condition data is currently available.
                      </Box>
                    </Box>
                  }
                  footer={
                    <Box textAlign="center" padding="s">
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          disabled={currentPageMarine === 1}
                          onClick={() => handlePageChange('marine', 'prev')}
                        >
                          Previous
                        </Button>
                        <Box variant="span">Page {currentPageMarine}</Box>
                        <Button
                          disabled={!nextTokenMarine}
                          onClick={() => handlePageChange('marine', 'next')}
                        >
                          Next
                        </Button>
                      </SpaceBetween>
                    </Box>
                  }
                />
                </SpaceBetween>
              </Container>
            )
          },
          {
            label: 'Trail Conditions',
            id: 'trail',
            content: (
              <Container header={<Header variant="h2">Trail Conditions Diagnostics</Header>}>
                <Table
                  columnDefinitions={[
                    {
                      id: 'locationId',
                      header: 'Trail ID',
                      cell: item => <code>{item.locationId}</code>
                    },
                    {
                      id: 'locationName',
                      header: 'Trail Name',
                      cell: item => item.locationName
                    },
                    {
                      id: 'status',
                      header: 'Status',
                      cell: item => getStatusIndicator(item.status)
                    },
                    {
                      id: 'lastUpdate',
                      header: 'Last Update',
                      cell: item => item.lastUpdate
                    },
                    {
                      id: 'condition',
                      header: 'Condition',
                      cell: item => {
                        const condition = item.payload?.condition
                        let color: 'green' | 'blue' | 'red' | 'grey' = 'grey'
                        switch (condition) {
                          case 'excellent': color = 'green'; break
                          case 'good': color = 'blue'; break
                          case 'fair': color = 'red'; break
                          case 'poor': color = 'red'; break
                        }
                        return <Badge color={color}>{condition || 'Unknown'}</Badge>
                      }
                    },
                    {
                      id: 'snowDepth',
                      header: 'Snow Depth',
                      cell: item => `${item.payload?.snowDepth || 'N/A'} cm`
                    }
                  ]}
                  items={diagnosticData.filter(item => item.dataProduct === 'trail')}
                  loading={loading}
                  empty={
                    <Box textAlign="center" color="inherit">
                      <b>No trail data</b>
                      <Box variant="p" color="inherit">
                        No trail condition data is currently available.
                      </Box>
                    </Box>
                  }
                  footer={
                    <Box textAlign="center" padding="s">
                      <SpaceBetween direction="horizontal" size="xs">
                        <Button
                          disabled={currentPageTrails === 1}
                          onClick={() => handlePageChange('trail', 'prev')}
                        >
                          Previous
                        </Button>
                        <Box variant="span">Page {currentPageTrails}</Box>
                        <Button
                          disabled={!nextTokenTrails}
                          onClick={() => handlePageChange('trail', 'next')}
                        >
                          Next
                        </Button>
                      </SpaceBetween>
                    </Box>
                  }
                />
              </Container>
            )
          },
          {
            label: 'NWS Observations',
            id: 'nws',
            content: (
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <Button 
                        onClick={refreshData}
                        loading={refreshing}
                      >
                        Refresh Data
                      </Button>
                    }
                  >
                    NWS Observations Diagnostics
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <SpaceBetween direction="horizontal" size="xs">
                    <input
                      type="text"
                      placeholder="Search by station ID (e.g., KORD)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch()
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d5dbdb',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <Button onClick={handleSearch} disabled={!searchQuery.trim()}>
                      Search
                    </Button>
                    {searchQuery && (
                      <Button onClick={handleClearSearch}>
                        Clear
                      </Button>
                    )}
                  </SpaceBetween>
                  <Table
                    columnDefinitions={[
                      {
                        id: 'locationId',
                        header: 'Station ID',
                        cell: item => <code>{item.locationId}</code>
                      },
                      {
                        id: 'locationName',
                        header: 'Station Name',
                        cell: item => item.locationName
                      },
                      {
                        id: 'status',
                        header: 'Status',
                        cell: item => getStatusIndicator(item.status)
                      },
                      {
                        id: 'lastUpdate',
                        header: 'Last Update',
                        cell: item => item.lastUpdate
                      },
                      {
                        id: 'temperature',
                        header: 'Temperature',
                        cell: item => {
                          const temp = item.payload?.temperature
                          if (temp === null || temp === undefined || temp === 'N/A') {
                            return 'N/A'
                          }
                          return `${temp}°C`
                        }
                      },
                      {
                        id: 'windSpeed',
                        header: 'Wind Speed',
                        cell: item => {
                          const speed = item.payload?.windSpeed
                          if (speed === null || speed === undefined || speed === 'N/A') {
                            return 'N/A'
                          }
                          return `${speed} km/h`
                        }
                      },
                      {
                        id: 'relativeHumidity',
                        header: 'Humidity',
                        cell: item => {
                          const humidity = item.payload?.relativeHumidity
                          if (humidity === null || humidity === undefined || humidity === 'N/A') {
                            return 'N/A'
                          }
                          return `${humidity}%`
                        }
                      },
                      {
                        id: 'visibility',
                        header: 'Visibility',
                        cell: item => {
                          const vis = item.payload?.visibility
                          if (vis === null || vis === undefined || vis === 'N/A') {
                            return 'N/A'
                          }
                          return `${vis} m`
                        }
                      },
                      {
                        id: 'textDescription',
                        header: 'Description',
                        cell: item => item.payload?.textDescription || 'N/A'
                      }
                    ]}
                    items={diagnosticData.filter(item => item.dataProduct === 'nws')}
                    loading={loading}
                    empty={
                      <Box textAlign="center" color="inherit">
                        <b>No NWS data</b>
                        <Box variant="p" color="inherit">
                          No NWS observation data is currently available.
                        </Box>
                      </Box>
                    }
                    footer={
                      <Box textAlign="center" padding="s">
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            disabled={currentPageNws === 1}
                            onClick={() => handlePageChange('nws', 'prev')}
                          >
                            Previous
                          </Button>
                          <Box variant="span">Page {currentPageNws}</Box>
                          <Button
                            disabled={!nextTokenNws}
                            onClick={() => handlePageChange('nws', 'next')}
                          >
                            Next
                          </Button>
                        </SpaceBetween>
                      </Box>
                    }
                  />
                </SpaceBetween>
              </Container>
            )
          },
          {
            label: 'Beach Conditions',
            id: 'beach_conditions',
            content: (
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <Button 
                        onClick={refreshData}
                        loading={refreshing}
                      >
                        Refresh Data
                      </Button>
                    }
                  >
                    Beach Conditions Diagnostics
                  </Header>
                }
              >
                <SpaceBetween size="m">
                  <SpaceBetween direction="horizontal" size="xs">
                    <input
                      type="text"
                      placeholder="Search by station ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch()
                        }
                      }}
                      style={{
                        flex: 1,
                        padding: '8px 12px',
                        fontSize: '14px',
                        border: '1px solid #d5dbdb',
                        borderRadius: '4px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <Button onClick={handleSearch} disabled={!searchQuery.trim()}>
                      Search
                    </Button>
                    {searchQuery && (
                      <Button onClick={handleClearSearch}>
                        Clear
                      </Button>
                    )}
                  </SpaceBetween>
                  <Table
                    columnDefinitions={[
                      {
                        id: 'locationId',
                        header: 'Station ID',
                        cell: item => <code>{item.locationId}</code>
                      },
                      {
                        id: 'locationName',
                        header: 'Station Name',
                        cell: item => item.locationName
                      },
                      {
                        id: 'status',
                        header: 'Status',
                        cell: item => getStatusIndicator(item.status)
                      },
                      {
                        id: 'lastUpdate',
                        header: 'Last Update',
                        cell: item => item.lastUpdate
                      },
                      {
                        id: 'swimRisk',
                        header: 'Swim Risk',
                        cell: item => {
                          const risk = item.payload?.swimRisk
                          if (!risk || risk === 'N/A') return 'N/A'
                          let color: 'green' | 'blue' | 'red' | 'grey' = 'grey'
                          if (risk === 'Low') color = 'green'
                          else if (risk === 'Moderate') color = 'blue'
                          else if (risk === 'High') color = 'red'
                          return <Badge color={color}>{risk}</Badge>
                        }
                      },
                      {
                        id: 'surfHeight',
                        header: 'Surf Height',
                        cell: item => item.payload?.surfHeight || 'N/A'
                      },
                      {
                        id: 'uvIndex',
                        header: 'UV Index',
                        cell: item => item.payload?.uvIndex || 'N/A'
                      }
                    ]}
                    items={diagnosticData.filter(item => item.dataProduct === 'beach_conditions')}
                    loading={loading}
                    empty={
                      <Box textAlign="center" color="inherit">
                        <b>No beach conditions data</b>
                        <Box variant="p" color="inherit">
                          No beach conditions data is currently available.
                        </Box>
                      </Box>
                    }
                    footer={
                      <Box textAlign="center" padding="s">
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button
                            disabled={currentPageBeachConditions === 1}
                            onClick={() => handlePageChange('beach_conditions', 'prev')}
                          >
                            Previous
                          </Button>
                          <Box variant="span">Page {currentPageBeachConditions}</Box>
                          <Button
                            disabled={!nextTokenBeachConditions}
                            onClick={() => handlePageChange('beach_conditions', 'next')}
                          >
                            Next
                          </Button>
                        </SpaceBetween>
                      </Box>
                    }
                  />
                </SpaceBetween>
              </Container>
            )
          },
          {
            label: 'HF Propagation',
            id: 'hf_propagation',
            content: (
              <Container
                header={
                  <Header
                    variant="h2"
                    actions={
                      <Button
                        onClick={async () => {
                          setRefreshing(true)
                          await loadHfPropagationSummary()
                          setRefreshing(false)
                        }}
                        loading={refreshing}
                      >
                        Refresh Data
                      </Button>
                    }
                  >
                    HF Radio Propagation Diagnostics
                  </Header>
                }
              >
                {hfPropagationLoading ? (
                  <StatusIndicator type="loading">Loading HF Propagation data...</StatusIndicator>
                ) : hfPropagationSummary ? (
                  <SpaceBetween size="l">
                    <ColumnLayout columns={3} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">Grid Points Parsed</Box>
                        <div>{hfPropagationSummary.grid_points_parsed ?? hfPropagationSummary.summary_statistics?.grid_points_parsed ?? 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Last Collection Time</Box>
                        <div>{hfPropagationSummary.last_collection_time ?? hfPropagationSummary.summary_statistics?.last_collection_time ?? 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Product Valid At</Box>
                        <div>{hfPropagationSummary.product_valid_at ?? hfPropagationSummary.summary_statistics?.product_valid_at ?? 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Errors Count</Box>
                        <div>{hfPropagationSummary.errors_count ?? hfPropagationSummary.summary_statistics?.errors_count ?? 0}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Records Stored</Box>
                        <div>{hfPropagationSummary.records_stored ?? hfPropagationSummary.summary_statistics?.records_stored ?? 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Health Status</Box>
                        <StatusIndicator type={
                          (hfPropagationSummary.health_status ?? hfPropagationSummary.summary_statistics?.health_status) === 'healthy' ? 'success' :
                          (hfPropagationSummary.health_status ?? hfPropagationSummary.summary_statistics?.health_status) === 'warning' ? 'warning' : 'error'
                        }>
                          {hfPropagationSummary.health_status ?? hfPropagationSummary.summary_statistics?.health_status ?? 'Unknown'}
                        </StatusIndicator>
                      </div>
                    </ColumnLayout>
                    <ColumnLayout columns={2} variant="text-grid">
                      <div>
                        <Box variant="awsui-key-label">X-Ray Message</Box>
                        <div>{hfPropagationSummary.xray_message ?? hfPropagationSummary.summary_statistics?.xray_message ?? 'N/A'}</div>
                      </div>
                      <div>
                        <Box variant="awsui-key-label">Proton Message</Box>
                        <div>{hfPropagationSummary.proton_message ?? hfPropagationSummary.summary_statistics?.proton_message ?? 'N/A'}</div>
                      </div>
                    </ColumnLayout>
                  </SpaceBetween>
                ) : (
                  <Box textAlign="center" color="inherit">
                    <b>No HF Propagation data</b>
                    <Box variant="p" color="inherit">
                      No HF Radio Propagation diagnostic data is currently available.
                    </Box>
                  </Box>
                )}
              </Container>
            )
          }
        ]}
        onChange={({ detail }) => {
          const newProduct = detail.activeTabId
          setSelectedDataProduct(newProduct)
          
          // Load data for the newly selected tab if not already loaded
          const currentData = diagnosticData.filter(item => item.dataProduct === newProduct)
          if (newProduct === 'hf_propagation') {
            if (!hfPropagationSummary) {
              loadHfPropagationSummary()
            }
          } else if (currentData.length === 0) {
            if (newProduct === 'marine') {
              loadPageData('marine', undefined)
            } else if (newProduct === 'trail') {
              loadPageData('trail', undefined)
            } else if (newProduct === 'nws') {
              loadPageData('nws', undefined)
            } else if (newProduct === 'beach_conditions') {
              loadPageData('beach_conditions', undefined)
            }
          }
        }}
      />
    </SpaceBetween>
  )
}

export default Diagnostics